import type { ComponentProps, KeyboardEvent } from "react"
import { useEffect, useRef, useState } from "react"
import { ExpandMore } from "@mui/icons-material"
import designSystemStyles from "@shared/styles/designSystemStyles"
import styles from "@shared/styles/components/ComboBox.module.css"
import { HorizontalContainer, VerticalContainer } from "./Containers"
import { SystemIcon } from "./SystemIcon"
import { Text } from "./Texts"

export interface ComboBoxOption {
    id: string
    title: string
}

interface ComboBoxProps {
    options: ComboBoxOption[]
    value?: ComboBoxOption
    placeholder?: string
    maxVisibleOptions?: number
    onChange?: (value: ComboBoxOption) => void
    /** pass null to render the combo without a leading icon */
    iconType?: ComponentProps<typeof SystemIcon>["type"] | null
    className?: string
    disabled?: boolean
}

export const ComboBox: React.FC<ComboBoxProps> = ({
    options,
    value,
    placeholder = "Select",
    maxVisibleOptions = 5,
    onChange,
    iconType = undefined,
    className,
    disabled = false
}) => {
    const [isOpen, setIsOpen] = useState(false)
    const containerRef = useRef<HTMLDivElement>(null)

    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (!containerRef.current?.contains(event.target as Node)) {
                setIsOpen(false)
            }
        }
        document.addEventListener("mousedown", handleClickOutside)
        return () => document.removeEventListener("mousedown", handleClickOutside)
    }, [])

    const handleToggle = () => {
        if (disabled) return
        setIsOpen((prev) => !prev)
    }

    const handleSelect = (option: ComboBoxOption) => {
        if (disabled) return
        onChange?.(option)
        setIsOpen(false)
    }

    const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
        if (disabled) return
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault()
            handleToggle()
        }
    }

    // Trimmed here so any caller's option title (collaborator, punto, organización,
    // etc.) with stray leading/trailing whitespace doesn't break the ** bold
    // markdown below — CommonMark won't bold "** foo**" or "**foo **".
    const displayValue = (value?.title ?? placeholder).trim()
    const useMarkdown = Boolean(value)
    const listMaxHeight = `calc(${designSystemStyles.sizeItemHeight} * ${maxVisibleOptions})`

    return (
        <div
            ref={containerRef}
            className={[styles.container, "DSComboBox", className].filter(Boolean).join(" ")}
        >
            <div
                className={[styles.trigger, disabled ? styles.disabled : ""].filter(Boolean).join(" ")}
                onClick={handleToggle}
                role="button"
                tabIndex={disabled ? -1 : 0}
                aria-expanded={isOpen}
                onKeyDown={handleKeyDown}
            >
                <HorizontalContainer
                    gap={designSystemStyles.gapLg}
                    alignment="center"
                    justifyContent="space-between"
                    paddingLeft={designSystemStyles.paddingMd}
                    paddingRight={designSystemStyles.paddingMd}
                    paddingTop={designSystemStyles.paddingSm}
                    paddingBottom={designSystemStyles.paddingSm}
                    backgroundColor={designSystemStyles.colorBackgroundGray2}
                >
                    <HorizontalContainer gap={designSystemStyles.gapSm} alignment="center">
                        {iconType && (
                            <SystemIcon type={iconType} color={designSystemStyles.colorText} />
                        )}
                        <Text
                            text={useMarkdown ? `**${displayValue}**` : displayValue}
                            markdown={useMarkdown}
                            className={styles.label}
                            color={designSystemStyles.colorText}
                        />
                    </HorizontalContainer>
                    <ExpandMore className={styles.chevron} />
                </HorizontalContainer>
            </div>
            {isOpen && (
                <VerticalContainer
                    gap={designSystemStyles.gapNone}
                    backgroundColor={designSystemStyles.colorBackgroundWhite}
                    className={styles.dropdown}
                >
                    <div className={styles.list} style={{ maxHeight: listMaxHeight }}>
                        {options.map((option) => (
                            <button
                                key={option.id}
                                type="button"
                                className={styles.option}
                                onClick={() => handleSelect(option)}
                                style={{ width: "100%" }}
                            >
                                <HorizontalContainer gap={designSystemStyles.gapSm} alignment="center">
                                    {iconType && (
                                        <SystemIcon type={iconType} color={designSystemStyles.colorText} />
                                    )}
                                    <Text
                                        text={option.title}
                                        markdown={useMarkdown}
                                        className={styles.optionLabel}
                                        color={designSystemStyles.colorText}
                                    />
                                </HorizontalContainer>
                            </button>
                        ))}
                    </div>
                </VerticalContainer>
            )}
        </div>
    )
}
